import { getDeeplinkBrowserUrl } from '@puzzlehq/frontend-utils';
import { NewUserQuest } from '@src/data/types';
import { useClaimReferralReward } from '@src/hooks/useReferrals';
import { useLatestRaffle } from '@src/hooks/useQuests';
import { NewUserQuestType } from 'jigsaw';
import { BiArrowBack, BiCheck, BiTimeFive } from 'react-icons/bi';
import { buttonVariants, HStack, Spinner, Text, VStack } from 'ui_2';
import { getTimeDiffString, renderPartTitle } from '@src/data/utils';
import { useEffect, useState } from 'react';
import { useDappState } from '@src/hooks/useDappState';

const getIsDeeplink = (url: string) => {
  return url.startsWith('https://app.puzzle.online');
};

type NewUserQuestRowViewProps = {
  url?: string;
  inProgress: boolean;
  completed: boolean;
  title: string;
  newUserQuestType?: NewUserQuestType;
  imageURL?: string;
  ctaText?: string;
};

export const QuestTimelineNode = ({
  state,
}: {
  state: 'completed' | 'incomplete';
}) => {
  return (
    <div className='relative z-10'>
      {state === 'completed' ? (
        <div className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-800'>
          <BiCheck color='white' size={20} />
        </div>
      ) : (
        <div className='h-[23px] w-[23px] shrink-0 rounded-full border-2 border-dashed border-gray-200 ' />
      )}
    </div>
  );
};

const CompletedNewUserQuest = ({ title }: NewUserQuestRowViewProps) => {
  return (
    <HStack className='w-fit items-center gap-2 rounded-[10px] bg-green-100 px-2 py-1'>
      <QuestTimelineNode state={'completed'} />
      <Text>{renderPartTitle(title)}</Text>
    </HStack>
  );
};

const IncompleteNewUserQuest = ({ title }: NewUserQuestRowViewProps) => {
  return (
    <HStack className='w-full items-center gap-2 px-2 py-1'>
      <QuestTimelineNode state={'incomplete'} />
      <div className={`rounded-[10px] bg-gray-50 px-2 py-1`}>
        <Text>{renderPartTitle(title)}</Text>
      </div>
    </HStack>
  );
};

type GiveawayInfoProps = {
  winnerCount: number;
  cutoff: Date;
  prizeDescription: string;
};

const GiveawayInfo = ({
  winnerCount,
  cutoff,
  prizeDescription,
}: GiveawayInfoProps) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    setInterval(() => setNow(new Date()), 500);
  }, []);

  return (
    <VStack className='w-full gap-1 rounded-[10px] bg-white/70 px-4 py-2 shadow-md'>
      <HStack className='w-fit items-center gap-2 rounded-full bg-white px-2'>
        <BiTimeFive className='text-gray-600' />
        <Text font='sm'>{getTimeDiffString(cutoff, now)} remaining</Text>
      </HStack>
      <VStack className='-space-y-1'>
        {winnerCount > 1 ? (
          <Text>{winnerCount} winners receive</Text>
        ) : (
          <Text>{winnerCount} winner receives</Text>
        )}
        <Text font={prizeDescription.length > 15 ? 'medium-lg' : 'medium-xl'}>
          {prizeDescription}
        </Text>
      </VStack>
    </VStack>
  );
};

const InProgressNewUserQuest = ({
  title,
  imageURL,
  ctaText,
  url,
  newUserQuestType,
}: NewUserQuestRowViewProps) => {
  const { claimReward, isLoading, isSuccess } = useClaimReferralReward();

  const showGiveawayUI = title.toLowerCase().includes('giveaway');
  const { data: giveaway } = useLatestRaffle(showGiveawayUI);

  const onClick = () => {
    if (newUserQuestType === NewUserQuestType.SignupReward) {
      claimReward();
      return;
    }
    if (!url) return;
    const isDeeplink = getIsDeeplink(url);
    let finalUrl: string;
    if (isDeeplink) {
      finalUrl = url;
    } else {
      finalUrl = getDeeplinkBrowserUrl(url, 'rootNavigationFlow');
    }

    window.open(finalUrl, '_blank');
  };

  return (
    <VStack className='overflow-clip rounded-[10px] border border-gray-100'>
      <HStack className='w-full items-center gap-2  px-2 py-1'>
        <QuestTimelineNode state={'incomplete'} />
        <div className={`flex-grow py-1`}>
          <Text>{renderPartTitle(title)}</Text>
        </div>
      </HStack>
      <div className='relative'>
        <img src={imageURL} />
        <VStack className='absolute inset-0 z-10 justify-between gap-4 p-4'>
          {giveaway && showGiveawayUI && (
            <div className='flex w-full justify-center'>
              <GiveawayInfo
                winnerCount={giveaway.winnerCount}
                cutoff={giveaway.cutoff}
                prizeDescription={giveaway.prizeDescription}
              />
            </div>
          )}
          {ctaText && (
            <div className='flex flex-grow flex-col items-end justify-end'>
              <button
                onClick={onClick}
                disabled={isLoading || isSuccess}
                className={buttonVariants({
                  variant: 'black',
                  className: 'w-fit items-center gap-2',
                })}
              >
                {ctaText}
                {isLoading ? (
                  <Spinner />
                ) : (
                  <BiArrowBack className='rotate-180' size={20} />
                )}
              </button>
            </div>
          )}
        </VStack>
      </div>
    </VStack>
  );
};

export const NewUserQuestRowView = ({
  url,
  inProgress,
  completed,
  title,
  newUserQuestType,
  ctaText,
  imageURL,
}: NewUserQuestRowViewProps) => {
  if (completed) {
    return (
      <CompletedNewUserQuest
        title={title}
        completed={completed}
        inProgress={inProgress}
        ctaText={ctaText}
        imageURL={imageURL}
      />
    );
  } else if (inProgress) {
    return (
      <InProgressNewUserQuest
        title={title}
        completed={completed}
        inProgress={inProgress}
        ctaText={ctaText}
        imageURL={imageURL}
        url={url}
        newUserQuestType={newUserQuestType}
      />
    );
  } else {
    return (
      <IncompleteNewUserQuest
        title={title}
        completed={completed}
        inProgress={inProgress}
        ctaText={ctaText}
        imageURL={imageURL}
      />
    );
  }
};

export const NewUserQuestRow = ({
  newUserQuest,
}: {
  newUserQuest: NewUserQuest;
}) => {
  return (
    <NewUserQuestRowView
      completed={newUserQuest.completed}
      inProgress={newUserQuest.inProgress}
      title={newUserQuest.title}
      url={newUserQuest.url}
      newUserQuestType={newUserQuest.type}
      ctaText={newUserQuest.ctaText}
      imageURL={newUserQuest.imageURL}
    />
  );
};
